<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :is-valid="isModalValid"
    :loading="isLoading"
    v-bind="{ ...dialogBinds }"
    @agree="agree"
  >
    <template #body>
      <v-col :cols="hasFilter ? 4 : 5" class="border-right pt-8">
        <v-form v-model="isValidName" ref="form-name">
          <v-row no-gutters align="center">
            <v-col cols="4" align-self="start" class="text-left pt-2 pl-4">
              <span> {{ $i18n.t('sidebar.locationLab.reportName') }}: </span>
            </v-col>
            <v-spacer />
            <v-col cols="7" class="pr-5" style="min-height: 70px">
              <data-input
                v-model="report.name"
                :data-type="{ name: 'text' }"
                :rules="[v => !!v || $i18n.t('rules.required')]"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-form v-model="isValidRegions" ref="form-regions" class="border-bottom">
          <v-row no-gutters>
            <v-col class="mb-6 pr-5 pl-4">
              <v-checkbox v-model="isRestrictRegionActive" class="mt-0 pt-0" hide-details>
                <template #label>
                  <div>
                    <span style="color: #202124 !important" class="text-body-2">
                      {{ $i18n.t('dialog.body.restrictReportToRegion') }}
                    </span>
                    <italic-subheader :translationPath="$i18n.t('dialog.body.restrictReportToRegionHint')" />
                  </div>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <main-input-dialog-row
            v-for="input in regionInputs"
            v-bind="input"
            :key="input.name"
            v-model="report.regions[input.name]"
            class="mb-6 pr-5 pl-4"
            align="start"
            hide-details
          >
            <template #[`features_id.input`]>
              <v-col cols="7">
                <v-select
                  v-model="report.regions['features_id']"
                  class="mt-0 pt-0"
                  :disabled="Boolean(!report.regions.attribute || isRegionLoading)"
                  :loading="isRegionLoading"
                  :auto-convert-select="false"
                  :rules="regionRules"
                  :items="regionDataSourceFeaturesList"
                  item-text="attribute"
                  item-value="id"
                  hide-details
                  outlined
                  multiple
                  chips
                  dense
                >
                  <template #prepend-item>
                    <v-list-item @click="toggleSelectAll">
                      <v-list-item-action>
                        <v-icon :color="report.regions['features_id'].length > 0 ? 'primary' : ''">
                          {{ iconSelectAll }}
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-content class="text-left">
                        <v-list-item-title>
                          {{ $i18n.t('button.selectAllItems') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2" />
                  </template>
                  <template #selection="{ item, index }">
                    <v-chip v-if="index < 3">
                      <span>{{ item.attribute }}</span>
                    </v-chip>
                    <span v-if="index === 3" class="text-caption grey--text">
                      {{ `(+${$i18n.tc('dialog.body.regions', report.regions['features_id'].length - 3)})` }}
                    </span>
                  </template>
                </v-select>
              </v-col>
            </template>
            <template #[`split.input`]>
              <v-checkbox v-model="report.regions['split']" class="mt-0 pt-0" hide-details>
                <template #label>
                  <div>
                    <span style="color: #202124 !important" class="text-body-2">
                      {{ $i18n.t('dialog.body.splitReport') }}
                    </span>
                    <italic-subheader :translationPath="$i18n.t('dialog.body.splitReportHint')" />
                  </div>
                </template>
              </v-checkbox>
            </template>
          </main-input-dialog-row>
        </v-form>
        <v-row no-gutters>
          <v-col cols="12" class="pr-5 pt-8">
            <v-row no-gutters>
              <v-spacer />
              <v-col cols="auto">
                <base-button class="mr-0" translation-path="sidebar.locationLab.addStatistic" @click="addStatistic" />
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" class="pl-4 pt-1">
                <v-list dense>
                  <v-list-item
                    v-for="(statistic, idx) in report.statistics"
                    :key="idx"
                    class="list-element hover-list-element px-2"
                    :class="{ 'list-element-active': editingStatisticIdx === idx }"
                    style="height: 45px"
                    @click="setActiveStatistic(idx)"
                  >
                    <v-list-item-content>
                      <v-list-item-title
                        :title="statistic.name"
                        v-text="statistic.name"
                        class="text-body-2 font-weight-regular"
                      />
                    </v-list-item-content>
                    <v-list-item-action>
                      <dots-menu
                        font-size="19px"
                        :items="statisticDotsItems"
                        @delete="deleteStatistic(idx)"
                        @edit="setActiveStatistic(idx)"
                      ></dots-menu>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col :cols="hasFilter ? 8 : 7" class="pt-8 px-8">
        <v-form class="statistic-form" ref="form-statistic" v-model="isValidStatistic" v-if="isAnyStatisticEditing">
          <main-input-dialog-row
            style="min-height: 68px"
            v-for="input in statisticInputs"
            v-bind="input"
            :key="input.name"
            v-model="report.statistics[editingStatisticIdx][input.name]"
            @change="onStatisticAttributeChange(input.name, $event)"
          >
            <template #[`filter_expression.input`]>
              <v-col cols="12">
                <v-row no-gutters>
                  <v-col cols="6">
                    <data-input
                      :data-type="{ name: 'checkbox' }"
                      :disabled="!currentStatistic.layer"
                      v-model="hasFilter"
                      @change="setFilter"
                    >
                      <template #label>
                        <span :class="{ 'text--disabled': !currentStatistic.layer }">
                          {{ $i18n.t('sidebar.locationLab.additionalCondition') }}
                        </span>
                      </template>
                    </data-input>
                  </v-col>
                </v-row>
                <v-row no-gutters v-if="currentStatistic.layer && hasFilter">
                  <v-col cols="12">
                    <expression-builder
                      ref="location-lab-expression-builder"
                      v-model="report.statistics[editingStatisticIdx][input.name]"
                      :main-data-source="layersList[currentStatistic.layer].dataSourceName"
                      :is-data-source-selectable="false"
                      :is-valid.sync="isValidFilter"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </template>
          </main-input-dialog-row>
        </v-form>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import ExpressionBuilder from '@/components/ExpressionBuilder';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import MainLargeDialog from '@/components/MainLargeDialog';
import ItalicSubheader from '@/components/ItalicSubheader';
export default {
  name: 'LocationLabStatisticsDialog',
  components: { DataInput, DotsMenu, ExpressionBuilder, MainInputDialogRow, MainLargeDialog, ItalicSubheader },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    report: {
      type: Object,
    },
  },
  computed: {
    statistics: get('admin/modulesMapping@location_lab.statistics'),
    statisticsAllowedDataTypes() {
      const obj = {};
      for (const statistic of this.statistics) {
        obj[statistic.name] = statistic.allowed_data_types;
      }
      return obj;
    },
    layersMetaData: get('layers/metadata'),
    layers: get('layers/layers'),
    featuresLayersArray() {
      return this.$_objectToArray(this.layers).filter(layer => layer.type === 'features_layer');
    },
    layersListArray() {
      return this.featuresLayersArray.map(layer => {
        const { id, name, data_source_name: dataSourceName, form_schema } = layer;
        const attributes = this.$_flattenStructure(form_schema.elements, 'elements');
        const idName = this.layersMetaData[dataSourceName]?.attributes_schema.id_name;
        const mappedAttributes = attributes.map(attribute => {
          const { attribute: name, label } = attribute;
          const dataSourceAttribute = this.layersMetaData[dataSourceName]?.attributes_schema.attributes.find(
            attribute => attribute.name === name
          );
          const { data_type } = dataSourceAttribute;
          return { name, label, type: data_type.name };
        });
        return { id, name, dataSourceName, attributes: mappedAttributes, idName };
      });
    },
    layersList() {
      return this.$_arrayToObject(this.layersListArray, 'id');
    },
    chartTypes() {
      return [
        { value: 'bar_horizontal', text: this.$i18n.t('sidebar.locationLab.horizontalBar') },
        { value: 'bar_vertical', text: this.$i18n.t('sidebar.locationLab.verticalBar') },
      ];
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    currentStatistic() {
      return this.report.statistics[this.editingStatisticIdx];
    },
    currentStatisticType() {
      return this.currentStatistic?.type;
    },
    isCurrentStatisticQuantity() {
      return this.currentStatistic?.type === 'quantity';
    },
    dialogBinds() {
      return {
        agreeText: this.new ? 'dialog.agreeAdd' : 'dialog.agreeSave',
        dialogWidth: this.hasFilter ? '80%' : '60%',
        headerShadow: true,
        minBodyHeight: '50vh',
        maxHeight: '90vh',
        noPaddingsX: true,
        noPaddingsY: true,
        title: this.new
          ? this.$i18n.t('sidebar.locationLab.addLocationLabReport')
          : this.$i18n.t('sidebar.locationLab.editLocationLabReport'),
      };
    },
    isAnyStatisticEditing() {
      return this.editingStatisticIdx || this.editingStatisticIdx === 0;
    },
    isModalValid() {
      return Boolean(
        this.report?.statistics?.length > 0 &&
          this.isValidRegions &&
          this.isValidName &&
          (!this.isAnyStatisticEditing || this.isValidStatistic) &&
          (!this.hasFilter || this.isValidFilter)
      );
    },
    new() {
      return !this.report?.id && this.report?.id !== 0;
    },
    statisticInputs() {
      return [
        {
          name: 'name',
          translationPath: 'sidebar.locationLab.statisticName',
          cols: this.hasFilter ? '9' : '12',
          rules: [v => !!v || v === 0 || 'rules.required'],
          dataType: { name: 'text' },
        },
        {
          name: 'layer',
          translationPath: 'sidebar.locationLab.layer',
          cols: this.hasFilter ? '9' : '12',
          rules: [v => !!v || v === 0 || 'rules.required'],
          dataType: { name: 'select' },
          items: this.layersListArray,
          itemValue: 'id',
          itemText: 'name',
        },
        {
          name: 'type',
          translationPath: 'sidebar.locationLab.statistic',
          cols: this.hasFilter ? '9' : '12',
          rules: [v => !!v || v === 0 || 'rules.required'],
          dataType: { name: 'select' },
          items: this.statistics,
          itemValue: 'name',
          itemText: 'verbose_name',
          disabled: !this.currentStatistic?.layer,
        },
        {
          name: 'aggregate_attribute_name',
          translationPath: 'sidebar.locationLab.aggregateAttribute',
          cols: this.hasFilter ? '9' : '12',
          dataType: { name: 'select' },
          items: this.layersList[this.currentStatistic?.layer]?.attributes,
          itemValue: 'name',
          itemText: 'label',
          disabled: !this.currentStatistic?.type,
          clearable: true,
        },
        {
          name: 'attribute_name',
          translationPath: 'sidebar.locationLab.countingAtribute',
          cols: this.hasFilter ? '9' : '12',
          rules: [v => !!v || v === 0 || 'rules.required'],
          dataType: { name: 'select' },
          items: this.layersList[this.currentStatistic?.layer]?.attributes.filter(attribute =>
            this.statisticsAllowedDataTypes[this.currentStatistic?.type]?.includes(attribute?.type)
          ),
          itemValue: 'name',
          itemText: 'label',
          disabled: !this.currentStatistic?.type || this.isCurrentStatisticQuantity,
        },
        {
          name: 'unit',
          translationPath: 'sidebar.locationLab.unit',
          cols: this.hasFilter ? '9' : '12',
          dataType: { name: 'text' },
        },
        {
          name: 'with_chart',
          label: this.$i18n.t('sidebar.locationLab.generateChart'),
          cols: '12',
          dataType: { name: 'checkbox' },
          leftCol: 0,
          disabled: !this.currentStatistic?.aggregate_attribute_name || this.report.regions?.split,
        },
        {
          name: 'chart_type',
          translationPath: 'sidebar.locationLab.chartType',
          cols: this.hasFilter ? '9' : '12',
          rules: [v => !this.currentStatistic?.with_chart || !!v || v === 0 || 'rules.required'],
          dataType: { name: 'select' },
          items: this.chartTypes,
          itemValue: 'value',
          itemText: 'text',
          disabled: !this.currentStatistic?.with_chart || this.report.regions?.split,
          disabledTitle: !this.currentStatistic?.with_chart || this.report.regions?.split,
          returnObject: false,
        },
        {
          name: 'filter_expression',
          label: this.$i18n.t('sidebar.locationLab.additionalCondition'),
          cols: '12',
          dataType: { name: 'checkbox' },
          leftCol: 0,
        },
      ];
    },
    regionInputs() {
      const rowStyle = {
        cols: 12,
        leftCol: 5,
        rightCol: 7,
      };
      return this.isRestrictRegionActive
        ? [
            {
              name: 'datasource',
              dataType: { name: 'select' },
              translationPath: 'dialog.body.dataSource',
              items: this.polygonDataSources,
              itemText: 'verbose_name',
              itemValue: 'name',
              rules: [v => !!v || this.$i18n.t('rules.required')],
              ...rowStyle,
            },
            {
              name: 'attribute',
              dataType: { name: 'select' },
              translationPath: 'dialog.body.regionAttribute',
              loading: this.isRegionLoading,
              items: this.dataSourceAttributes,
              itemText: 'verbose_name',
              itemValue: 'id',
              disabled: !this.selectedDataSource || this.isRegionLoading,
              rules: [v => !!v || this.$i18n.t('rules.required')],
              ...rowStyle,
            },
            {
              name: 'features_id',
              translationPath: 'dialog.body.region',
              leftCol: 5,
            },
            ...(this.report.regions?.features_id?.length > 1
              ? [
                  {
                    name: 'split',
                    leftCol: 0,
                  },
                ]
              : []),
          ]
        : [];
    },
    regionRules() {
      return [v => v?.length > 0 || this.$i18n.t('rules.required')];
    },
    polygonDataSources() {
      return this.$_objectToArray(this.layersMetaData).filter(datasource =>
        datasource.geometry_type?.includes('polygon')
      );
    },
    selectedDataSource() {
      return this.layersMetaData[this.report.regions?.datasource];
    },
    dataSourceAttributes() {
      return (this.selectedDataSource?.attributes_schema.attributes || []).filter(attribute =>
        ['text', 'integer', 'float', 'decimal'].includes(attribute.data_type.name)
      );
    },
    dataSourcesFeatures: get('layers/dataSourcesFeatures'),
    allRegionsSelected() {
      return this.report.regions['features_id']?.length === this.regionDataSourceFeaturesList.length;
    },
    someRegionsSelected() {
      return this.report.regions['features_id']?.length > 0 && !this.allRegionsSelected;
    },
    iconSelectAll() {
      if (this.allRegionsSelected) return 'mdi-checkbox-marked';
      if (this.someRegionsSelected) return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },
  },
  data: () => ({
    editingStatisticIdx: null,
    isLoading: false,
    isValidFilter: false,
    isValidName: false,
    isValidRegions: false,
    isValidStatistic: false,
    statisticDotsItems: [{ name: 'edit' }, { name: 'delete' }],
    hasFilter: false,
    isRestrictRegionActive: false,
    isRegionLoading: false,
    regionDataSourceFeaturesList: [],
  }),
  methods: {
    addLocationLabReport: call('locationLab/addLocationLabReport'),
    editLocationLabReport: call('locationLab/editLocationLabReport'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    async getRegionDataSourcesFeatures(datasource = this.report?.regions?.datasource, clearSubProps = false) {
      if (clearSubProps) {
        this.$set(this.report.regions, 'attribute', '');
        this.$set(this.report.regions, 'features_id', []);
        this.$refs?.['form-regions']?.resetValidation();
      }
      if (!datasource) return;
      this.isRegionLoading = true;
      try {
        await this.getDataSourceFeatures({ dataSource: datasource });
        if (!clearSubProps && this.report.regions.attribute)
          this.getRegionDataSourceFeaturesList(datasource, this.report.regions.attribute);
      } finally {
        this.isRegionLoading = false;
      }
    },
    getRegionDataSourceFeaturesList(
      datasource = this.report?.regions?.datasource,
      attribute = this.report?.regions?.attribute
    ) {
      this.$refs?.['form-regions']?.resetValidation();
      if (!datasource || !attribute) {
        this.regionDataSourceFeaturesList = [];
        this.$set(this.report.regions, 'features_id', []);
        return;
      }
      const attributeName = this.dataSourceAttributes?.find(attr => attr.id === attribute).name;
      this.regionDataSourceFeaturesList =
        this.dataSourcesFeatures[datasource]?.features.map(feature => {
          return {
            id: feature.id,
            attribute: feature.properties[attributeName] || feature.id,
          };
        }) || [];
    },
    async add() {
      this.isLoading = true;
      if (!this.isRestrictRegionActive) {
        delete this.report.regions;
      } else if (this.isRestrictRegionActive && this.report?.regions?.split) {
        this.report.statistics = this.report.statistics.map(statistic => {
          return {
            ...statistic,
            with_chart: false,
            chart_type: null,
          };
        });
      } else if (this.isRestrictRegionActive && !this.report?.regions?.split) {
        this.report.regions.split = false;
      }
      try {
        await this.addLocationLabReport(this.report);
        this.computedIsVisible = false;
      } finally {
        this.isLoading = false;
      }
    },
    async edit() {
      this.isLoading = true;
      try {
        const { id, name, statistics } = this.report;
        let updated_statistics = statistics.filter(statistic => statistic.id || statistic.id == 0);
        let created_statistics = statistics.filter(statistic => !statistic.id && statistic.id != 0);
        if (this.report?.regions?.split) {
          updated_statistics = updated_statistics.map(statistic => {
            return {
              ...statistic,
              with_chart: false,
              chart_type: null,
            };
          });
          created_statistics = created_statistics.map(statistic => {
            return {
              ...statistic,
              with_chart: false,
              chart_type: null,
            };
          });
        } else {
          this.report.regions.split = false;
        }
        const regions = this.isRestrictRegionActive ? this.report.regions : undefined;
        const data = {
          id,
          payload: {
            name,
            updated_statistics,
            created_statistics,
            regions,
          },
        };
        await this.editLocationLabReport(data);
        this.computedIsVisible = false;
      } finally {
        this.isLoading = false;
      }
    },
    agree() {
      this.new ? this.add() : this.edit();
    },
    addStatistic() {
      if ((!this.isValidStatistic || (this.hasFilter && !this.isValidFilter)) && this.isAnyStatisticEditing) {
        this.$refs?.['form-statistic']?.validate();
        this.$refs?.['location-lab-expression-builder']?.[0]?.$refs?.form?.validate();
        return;
      }
      this.report.statistics.unshift({
        name: this.$i18n.t('sidebar.locationLab.newStatistic'),
        layer: null,
        type: '',
        attribute_name: '',
        aggregate_attribute_name: null,
        unit: null,
        with_chart: false,
        chart_type: null,
        filter_expression: null,
      });
      this.setActiveStatistic(0);
    },
    deleteStatistic(idx) {
      this.report.statistics.splice(idx, 1);
      this.editingStatisticIdx = null;
      this.hasFilter = false;
    },
    onStatisticAttributeChange(attribute, value) {
      if (attribute === 'layer') {
        this.report.statistics[this.editingStatisticIdx].type = '';
        this.hasFilter = false;
        this.report.statistics[this.editingStatisticIdx].filter_expression = null;
      } else if (attribute === 'type') {
        this.report.statistics[this.editingStatisticIdx].attribute_name = '';
        this.report.statistics[this.editingStatisticIdx].aggregate_attribute_name = null;
      } else if (attribute === 'with_chart') {
        this.report.statistics[this.editingStatisticIdx].chart_type = value ? this.chartTypes[1] : null;
      }
    },
    setActiveStatistic(idx) {
      if ((!this.isValidStatistic || (this.hasFilter && !this.isValidFilter)) && this.isAnyStatisticEditing) {
        this.$refs?.['form-statistic']?.validate();
        this.$refs?.['location-lab-expression-builder']?.[0]?.$refs?.form?.validate();
        return;
      }
      this.editingStatisticIdx = idx;
      setTimeout(() => {
        this.hasFilter = this.report.statistics?.[this.editingStatisticIdx]?.filter_expression ? true : false;
        this.$refs?.['form-statistic']?.resetValidation();
      });
    },
    setFilter() {
      if (this.report.statistics[this.editingStatisticIdx]) {
        this.report.statistics[this.editingStatisticIdx].filter_expression = null;
      }
    },
    toggleSelectAll() {
      this.$nextTick(() => {
        if (this.allRegionsSelected) {
          this.report.regions['features_id'] = [];
        } else {
          this.report.regions['features_id'] = this.regionDataSourceFeaturesList.map(region => region.id);
        }
      });
    },
  },
  watch: {
    computedIsVisible(nV) {
      this.editingStatisticIdx = null;
      this.$refs?.['form-statistic']?.resetValidation();
      this.$refs?.['form-name']?.resetValidation();
      this.$refs?.['form-regions']?.resetValidation();
      if (nV) {
        this.hasFilter = false;
      }
    },
    'currentStatistic.type'(nV) {
      if (nV === 'quantity') {
        const idName = this.layersList[this.currentStatistic.layer].idName;
        this.report.statistics[this.editingStatisticIdx].attribute_name = idName;
      }
    },
    'currentStatistic.aggregate_attribute_name'(nV) {
      delete this.report.statistics[this.editingStatisticIdx]?.aggregate_attribute;
      if (!nV && this.report.statistics[this.editingStatisticIdx]?.with_chart) {
        this.report.statistics[this.editingStatisticIdx].with_chart = false;
      }
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const {
          report: {
            regions: { datasource },
          },
        } = vm;
        return datasource;
      },
      (nV, oV) => {
        this.getRegionDataSourcesFeatures(nV, !((!oV && nV) || oV === nV));
      },
      { immediate: true }
    );
    this.$watch(
      vm => {
        const {
          report: {
            regions: { attribute },
          },
        } = vm;
        return attribute;
      },
      nV => {
        const datasource = this.report?.regions?.datasource || '';
        if (datasource) {
          this.isRestrictRegionActive = true;
        } else {
          this.isRestrictRegionActive = false;
        }
        if (nV && this.report.regions['features_id'].length > 0) {
          this.$set(this.report.regions, 'features_id', []);
        }
        this.getRegionDataSourceFeaturesList(datasource, nV);
      },
      { immediate: true }
    );
  },
};
</script>
<style lang="scss" scoped>
.border-bottom {
  @include border(bottom, 1px);
}
.border-right {
  @include border(right, 1px);
}
.list-element {
  @include border();
  text-align: left;
}
.list-element-active {
  background-color: #edf4fe !important;
  color: map-get($colors, 'primary') !important;
}
.statistic-form {
  ::v-deep {
    .v-input--checkbox {
      .v-input__slot {
        padding: 0 !important;
      }
      .v-label {
        font-family: Roboto;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 15px;
        color: #202124;
      }
    }
  }
}
::v-deep {
  .v-select .v-chip {
    margin: 2px !important;
  }
  .v-list-item__action {
    margin-right: 20px !important;
  }
  .v-list-item__title {
    text-align: left;
  }
}
</style>
